/**
 * Web Sign In Page
 *
 * Displays authentication UI for web users.
 * Adapted from desktop's SignInPrompt but without Electron APIs.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/button';
import { FeatureChip } from '../components/feature-chip';
import { KeycapLogo } from '../components/keycap-logo';
import { VideoEmbed } from '../components/video-embed';
import { usePlatform } from '../contexts/platform-context';
import { cn } from '../utils/cn';

type SignInPageProps = {
  onSignIn?: () => Promise<void> | void;
  isLoading?: boolean;
  className?: string;
};

export const SignInPage: React.FC<SignInPageProps> = ({
  onSignIn,
  isLoading = false,
  className,
}) => {
  const { isSidebar, webAppUrl, platform } = usePlatform();
  const navigate = useNavigate();
  const [shouldLoadVideo, setShouldLoadVideo] = React.useState(false);

  // Debug logging
  React.useEffect(() => {
    console.log('🔍 [SignInPage] Platform detection:', {
      platform,
      isSidebar,
      webAppUrl,
      urlParams: window.location.search,
      windowWidth: window.innerWidth,
    });
  }, [platform, isSidebar, webAppUrl]);

  // Delay video load to improve initial page render
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoadVideo(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleSignIn = async () => {
    // If in sidebar (extension), request parent to open web app with auto-login
    if (isSidebar) {
      const loginUrl = `${webAppUrl}/signin?autoLogin=true`;
      console.log('🔐 [SignIn] Sidebar detected, requesting parent to open:', loginUrl);

      // Post message to parent frame (sidepanel) to open new tab
      window.parent.postMessage(
        {
          type: 'OPEN_WEB_APP_LOGIN',
          url: loginUrl,
        },
        '*'
      );
      return;
    }

    // Normal web app flow
    if (onSignIn) {
      try {
        await onSignIn();
      } catch (error) {
        console.error('Sign in failed:', error);
      }
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center p-4 md:p-0 relative w-full',
        className
      )}
    >
      {/* Logo section - Smaller on mobile */}
      <div className="mt-6 md:mt-12 mb-4 md:mb-0 flex justify-center">
        <KeycapLogo className="h-10 md:h-20 w-auto" />
      </div>

      {/* Title - Responsive text sizes */}
      <div className="text-center mt-0 mb-4 px-4">
        <h1 className="text-text-one text-lg md:text-xl font-normal mb-2">
          Welcome to CTRL <span className="font-bold">ENTER</span>
        </h1>
        <p className="text-foreground text-sm md:text-base">
          Your AI Assistant for All of Healthcare
        </p>
      </div>

      {/* Video Embed and Features section - Stack on mobile, side-by-side on desktop */}
      <div className="w-full max-w-[900px] px-4 mb-4">
        <div className="glass-card rounded-2xl p-3 md:p-4 flex flex-col md:flex-row items-stretch gap-3 md:gap-4">
          {/* Video - Full width on mobile, 2/3 on desktop */}
          <div className="w-full md:w-2/3 flex items-center justify-center">
            {shouldLoadVideo ? (
              <VideoEmbed
                src="https://player.vimeo.com/video/1140548484?badge=0&autopause=0&player_id=0&app_id=58479"
                title="CTRL ENTER Introduction Video"
                aspectRatio="16/9"
                className="rounded-xl md:rounded-2xl"
              />
            ) : (
              <div className="w-full aspect-video flex items-center justify-center bg-muted/20 rounded-xl md:rounded-2xl">
                <div className="text-muted-foreground text-sm">Loading video...</div>
              </div>
            )}
          </div>

          {/* Features - Full width on mobile, 1/3 on desktop */}
          <div className="w-full md:w-1/3 flex flex-col justify-between gap-2 md:gap-2">
            <FeatureChip
              title="10x Productivity"
              description="From prior auths to coding checks, complete workflows in minutes instead of hours."
              icon="/rocket.svg"
            />
            <FeatureChip
              title="Screen Aware Intelligence"
              description="Instantly spot errors, missing fields, and next steps. Let your team work smarter, not harder."
              icon="/monitor.svg"
            />
            <FeatureChip
              title="HIPAA Compliant"
              description="Harness AI confidently with enterprise-grade security and compliance baked in."
              icon="/shield.svg"
            />
          </div>
        </div>
      </div>

      {/* Sign In Button and Footer text */}
      <div className="flex flex-col items-center justify-center w-full px-4">
        <div className="flex flex-col w-full md:w-80 gap-3 mb-4">
          {/* Sign In Button */}
          <Button
            onClick={handleSignIn}
            disabled={isLoading}
            variant="primary"
            className="w-full h-12 px-8 rounded-xl text-base font-medium shadow-lg hover:shadow-xl transition-all duration-200"
            size="lg"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Signing In...
              </>
            ) : isSidebar ? (
              <>
                Sign In in Web App
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </>
            ) : (
              'Sign Up / Sign In'
            )}
          </Button>

          {/* Try Me — free guest chat, only on the full web app (not sidebar) */}
          {!isSidebar && (
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="secondary"
                className="w-full h-12 px-8 rounded-xl text-base font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                size="lg"
                onClick={() => navigate('/try')}
              >
                Try for free
              </Button>
              <p className="text-[10px] text-muted-foreground/50">
                No sign-up required · Up to 10 free chats
              </p>
            </div>
          )}
        </div>

        {/* Sidebar helper text */}
        {isSidebar && (
          <div className="mb-4 text-center px-4">
            <p className="text-xs text-muted-foreground">
              Authentication is only available in the full web app. Click the button to open it in a
              new tab.
            </p>
          </div>
        )}

        {/* Footer text - Responsive sizing */}
        <div className="text-center mb-4 px-4">
          <p className="text-bg-neutral-6 text-[10px] md:text-[10px] mb-1 font-semibold">
            New to CTRL ENTER?{' '}
            <a
              href="https://www.enter.health"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-primary"
            >
              Learn More
            </a>
          </p>
          <p className="text-bg-neutral-6 text-[10px] md:text-[10px]">
            By signing in, you agree to our{' '}
            <a
              href="https://www.enter.health/legal/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-primary"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://www.enter.health/legal/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-primary"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

SignInPage.displayName = 'SignInPage';
